import React, { useEffect, useState, useRef } from "react";
import {
  Card,
  Row,
  Col,
  Form,
  Table,
  Spinner,
  Modal,
  Button,
} from "react-bootstrap";
import { io } from "socket.io-client";
import "./EmployeeLeaveApplication.css";
import { AiFillDelete } from "react-icons/ai";
import { toast, ToastContainer } from "react-toastify";
import axios from "axios";
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
const BORDER_COLORS = {
  Vacation: "#4da6ff",
  Sick: "#ff6666",
  Personal: "#ffcc66",
  Other: "#ffa500",
};
const EmployeeLeaveApplication = () => {
  const today = new Date();
  /* ------------------------------------------------------------------ */
  /* user & socket setup                                                 */
  /* ------------------------------------------------------------------ */
  const storedUser = JSON.parse(localStorage.getItem("user")) || {};
  const employeeId = storedUser.employeeId;
  const socketRef = useRef(null);

  /* ------------------------------------------------------------------ */
  /* state                                                               */
  /* ------------------------------------------------------------------ */
  const [escalatedEmployees, setEscalatedEmployees] = useState([]);
  const [selectedEscalationId, setSelectedEscalationId] = useState(employeeId);
  const [showModal, setShowModal] = useState(false);
  const [employeeLeaves, setEmployeeLeaves] = useState({
    unplannedLeave: 0,
    plannedLeave: 0,
    remainingUnplannedLeave: 0,
    remainingPlannedLeave: 0,
    pendingComoff: 0,
    completedComoff: 0,
  });
  const [year1, setYear1] = useState(today.getFullYear());
  const [leaveRecords, setLeaveRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [deleteLeaves, setDeleteLeaves] = useState({});

  /* helper: render numbers w/o trailing zeros */
  const formatNumber = (num) => {
    const str = num != null ? num.toString() : "0";
    return str.includes(".") ? str.replace(/\.?0+$/, "") : str;
  };

  /* ------------------------------------------------------------------ */
  /* socket connect (polling-only to avoid WS error)                     */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    socketRef.current = io(API_URL, {
      transports: ["polling"],
      upgrade: false,
    });

    socketRef.current.emit("join", { userId: storedUser.id });

    const refreshAll = () => {
      fetchLeaveBalances(selectedEscalationId);
      fetchLeaveRecords(selectedEscalationId);
    };

    socketRef.current.on("employeeLeavesChanged", refreshAll);
    socketRef.current.on("employeeLeavesDateChanged", refreshAll);

    // ← NEW: listen for comp-off settlement events too
    socketRef.current.on("compOffUpdated", () => {
      // only need to refresh comp-off counts:
      fetchLeaveBalances(selectedEscalationId);
    });

    return () => {
      socketRef.current.off("employeeLeavesChanged", refreshAll);
      socketRef.current.off("employeeLeavesDateChanged", refreshAll);
      socketRef.current.off("compOffUpdated");
      socketRef.current.disconnect();
    };
  }, [storedUser.id, selectedEscalationId]);

  /* ------------------------------------------------------------------ */
  /* fetch escalated hierarchy list                                      */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!employeeId) return;
    fetch(`${API_URL}/api/escalated-employees?empId=${employeeId}`)
      .then((r) => r.json())
      .then((list) => {
        setEscalatedEmployees(list);
        setSelectedEscalationId(employeeId);
      })
      .catch((err) => {
        console.error("Error loading escalations:", err);
        setEscalatedEmployees([{ id: employeeId, name: storedUser.name }]);
      });
  }, [employeeId, storedUser.name]);

  /* ------------------------------------------------------------------ */
  /* reusable fetchers                                                   */
  /* ------------------------------------------------------------------ */
  const fetchLeaveBalances = (empId) => {
    if (!empId) return;

    const params = new URLSearchParams({
      employeeId: empId,
      year: year1 || new Date().getFullYear(), // default current year
    });

    /* overall leave balances */
    fetch(`${API_URL}/api/employees-leaves?${params.toString()}`)
      .then((r) => r.json())
      .then((d) =>
        setEmployeeLeaves((prev) => ({
          ...prev,
          unplannedLeave: d.usedUnplannedLeave ?? 0,
          plannedLeave: d.usedPlannedLeave ?? 0,
          remainingUnplannedLeave: d.remainingUnplannedLeave ?? 0,
          remainingPlannedLeave: d.remainingPlannedLeave ?? 0,
        }))
      )
      .catch((err) => console.error("Error fetching leaves:", err));

    /* comp-off counts */
    fetch(`${API_URL}/api/employees-comoff/${empId}`)
      .then((r) => r.json())
      .then((d) =>
        setEmployeeLeaves((prev) => ({
          ...prev,
          pendingComoff: d.pendingComoff ?? 0,
          completedComoff: d.completedComoff ?? 0,
        }))
      )
      .catch((err) => {
        console.error("Error fetching compoff counts:", err);
        setEmployeeLeaves((prev) => ({
          ...prev,
          pendingComoff: 0,
          completedComoff: 0,
        }));
      });
  };

  const fetchLeaveRecords = (empId, year) => {
    if (!empId) return;
    setLoadingRecords(true);

    const params = new URLSearchParams({
      employeeId: empId,
      year: year1 || new Date().getFullYear(), // default current year
    });

    fetch(`${API_URL}/api/employeeleavesdate?${params.toString()}`)
      .then((r) => r.json())
      .then((d) => setLeaveRecords(Array.isArray(d) ? d : []))
      .catch((err) => {
        console.error("Error fetching leave records:", err);
        setLeaveRecords([]);
      })
      .finally(() => setLoadingRecords(false));
  };

  const handleDeleteLeave = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/delete-employee-leaves`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...deleteLeaves }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Delete failed");
      } else {
        toast.success(
          `${deleteLeaves.employee_name}'s ${deleteLeaves.leave_type} ${deleteLeaves.leave_day}-day leave on ${deleteLeaves.leave_date} has been deleted.`
        );
        setShowModal(false);
      }
      fetchLeaveBalances(selectedEscalationId);
      fetchLeaveRecords(selectedEscalationId);
    } catch (err) {
      const msg = err.message || "Delete leaves failed";
      toast.error(msg);
    }
  };

  /* ------------------------------------------------------------------ */
  /* refresh balances + records whenever viewed employee changes         */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    fetchLeaveBalances(selectedEscalationId);
    fetchLeaveRecords(selectedEscalationId);
  }, [selectedEscalationId, year1]);

  /* ------------------------------------------------------------------ */
  /* render                                                              */
  /* ------------------------------------------------------------------ */
  const handleYearChange = (e) => {
    const newYear = parseInt(e.target.value, 10);
    if (!isNaN(newYear)) {
      setYear1(newYear);
    }
  };

  return (
    <div
      style={{
        backgroundColor: "#f5f7fa",
        minHeight: "100vh",
        height: "100%",
      }}
      className="leave-application-container px-4 py-4"
    >
      <Row className="gx-4">
        <Col md={2}>
          <Form.Group>
            <Form.Label className="fw-semibold small">Year</Form.Label>
            <Form.Control
              type="number"
              min="2000"
              max="9999"
              value={year1}
              onChange={handleYearChange}
              size="sm"
            />
          </Form.Group>
        </Col>
        <Col lg={4} className="mb-3">
          <Form.Group controlId="escalate-to">
            <Form.Label>
              <b>View Records For</b>
            </Form.Label>
            <Form.Select
              value={selectedEscalationId}
              onChange={(e) => setSelectedEscalationId(Number(e.target.value))}
            >
              {escalatedEmployees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.name} {emp.id === employeeId ? "(You)" : ""}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row className="gx-4">
        {/* → balances */}
        <Col lg={6} className="mb-4">
          <Card className="shadow-sm leave-card">
            <Card.Header className="leave-card-header">
              <h5>Leave Balances</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="balances-section">
                    <h6>Used</h6>
                    <Form.Group controlId="used-unplanned" className="mb-2">
                      <Form.Label>Unplanned</Form.Label>
                      <Form.Control
                        readOnly
                        type="text"
                        value={formatNumber(employeeLeaves.unplannedLeave)}
                      />
                    </Form.Group>
                    <Form.Group controlId="used-planned" className="mb-3">
                      <Form.Label>Planned</Form.Label>
                      <Form.Control
                        readOnly
                        type="text"
                        value={formatNumber(employeeLeaves.plannedLeave)}
                      />
                    </Form.Group>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="balances-section">
                    <h6>Remaining</h6>
                    <Form.Group
                      controlId="remaining-unplanned"
                      className="mb-2"
                    >
                      <Form.Label>Unplanned</Form.Label>
                      <Form.Control
                        readOnly
                        type="text"
                        value={formatNumber(
                          employeeLeaves.remainingUnplannedLeave
                        )}
                      />
                    </Form.Group>
                    <Form.Group controlId="remaining-planned">
                      <Form.Label>Planned</Form.Label>
                      <Form.Control
                        readOnly
                        type="text"
                        value={formatNumber(
                          employeeLeaves.remainingPlannedLeave
                        )}
                      />
                    </Form.Group>
                  </div>
                </Col>
              </Row>

              <hr className="my-3" />

              <Row>
                <Col md={6}>
                  <Form.Group controlId="pending-comoff">
                    <Form.Label>Approved Comoff</Form.Label>
                    <Form.Control
                      readOnly
                      type="text"
                      value={formatNumber(employeeLeaves.pendingComoff)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="completed-comoff">
                    <Form.Label>Used Comoff</Form.Label>
                    <Form.Control
                      readOnly
                      type="text"
                      value={formatNumber(employeeLeaves.completedComoff)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        {/* → records */}
        <Col lg={6}>
          <Card className="shadow-sm leave-card">
            <Card.Header className="leave-card-header">
              <h5>
                Leave Records —{" "}
                {
                  escalatedEmployees.find((e) => e.id === selectedEscalationId)
                    ?.name
                }
              </h5>
            </Card.Header>
            <Card.Body className="record-table-container">
              {loadingRecords ? (
                <div className="text-center my-3">
                  <Spinner animation="border" variant="secondary" />
                </div>
              ) : (
                <Table bordered hover size="sm" className="leave-records-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>H/F</th>
                      {storedUser.role === 4 && <th></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {leaveRecords.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center">
                          No records found.
                        </td>
                      </tr>
                    ) : (
                      leaveRecords.map((rec) => (
                        <tr key={rec.id}>
                          <td>{rec.leave_date}</td>
                          <td>{rec.leave_type}</td>
                          <td>{rec.leave_day}</td>
                          {storedUser.role === 4 && (
                            <td>
                              <AiFillDelete
                                size={24}
                                color="#DC2525"
                                onClick={() => {
                                  setShowModal(true);
                                  setDeleteLeaves(rec);
                                }}
                              />
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title>Delete Leave</Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            maxHeight: "60vh",
            overflowY: "auto",
            padding: "1rem 1.5rem",
          }}
        >
          <div className="d-flex justify-content-between">
            <h6>
              Are you sure you want to delete the{" "}
              <span>{deleteLeaves.leave_type}</span>{" "}
              <span>{deleteLeaves.leave_day}-day</span> leave of{" "}
              <span className="text-primary">{deleteLeaves.employee_name}</span>{" "}
              on <span className="text-success">{deleteLeaves.leave_date}</span>
              ?
            </h6>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button size="sm" variant="primary" onClick={handleDeleteLeave}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default EmployeeLeaveApplication;
